import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { membershipReports } from "../api";
import { formatDate } from "../api/formatDate";
import MainStructure from "../components/containers/MainStructure";
import Pagination from "../components/elements/Pagination";
import SourAlert from "../components/elements/SourAlert";
import usePaginatedData from "../hooks/usePaginatedData";
import useSourAlert from "../hooks/useSourAlert";

function getReportStatus(report) {
	const status = report?.report?.status;
	if (status === "completed") {
		return { label: "Ready", modifier: "ready" };
	}
	if (status === "error") {
		return { label: "Needs attention", modifier: "error" };
	}
	if (status === "requested") {
		return { label: "Queued", modifier: "pending" };
	}
	return { label: "Generating", modifier: "pending" };
}

function Pedigree() {
	const [deletedIds, setDeletedIds] = useState([]);
	const [searchInput, setSearchInput] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [alertProps, showAlert] = useSourAlert();
	const {
		data,
		loading,
		currentPage,
		totalPages,
		handlePageChange,
		startIndex,
		endIndex,
	} = usePaginatedData(membershipReports.details, { limit: 10, query: searchTerm });
	const visibleReports = data.results.filter(
		(report) => !deletedIds.includes(report.id)
	);

	useEffect(() => {
		const timer = window.setTimeout(() => {
			const nextTerm = searchInput.trim();
			if (nextTerm !== searchTerm) {
				setSearchTerm(nextTerm);
				handlePageChange(1);
			}
		}, 350);
		return () => window.clearTimeout(timer);
	}, [searchInput, searchTerm, handlePageChange]);

	function clearSearch() {
		setSearchInput("");
		setSearchTerm("");
		handlePageChange(1);
	}

	function handleDelete(report) {
		showAlert({
			type: "warning",
			title: "Delete report",
			description: `Are you sure you want to delete ${
				report.name || "this report"
			}?`,
			showCancelButton: true,
			cancelButtonText: "Cancel",
			showOkButton: true,
			okButtonText: "Delete",
			onOk: () => {
				membershipReports
					.delete_report(report.id)
					.then(() => {
						setDeletedIds((previous) => [...previous, report.id]);
					})
					.catch(() => {
						showAlert({
							type: "error",
							title: "Report not deleted",
							description:
								"We could not delete this report. Please try again.",
						});
					});
			},
		});
	}

	return (
		<MainStructure>
			<main className="pedigree-reports">
				<header className="pedigree-reports__hero">
					<div>
						<p className="pedigree-reports__eyebrow">Pedigree analysis</p>
						<h1>Pedigree reports</h1>
						<p>Explore, share, and understand your pedigree analyses.</p>
					</div>
					<Link
						to="/new-pedigree"
						className="pedigree-reports__create"
						data-tour-id="pedigree-create-button"
					>
						<i className="fa fa-plus" aria-hidden="true" /> Create pedigree
					</Link>
				</header>
				<div className="pedigree-reports__search" role="search">
					<label htmlFor="pedigree-report-search">Search reports</label>
					<div>
						<input
							id="pedigree-report-search"
							type="search"
							value={searchInput}
							onChange={(event) => setSearchInput(event.target.value)}
							placeholder="Report, sire, or dam name"
						/>
					</div>
				</div>

				<section
					className="pedigree-reports-panel"
					aria-labelledby="all-reports-title"
				>
					<div className="pedigree-reports-panel__header">
						<div>
							<h2 id="all-reports-title">All reports</h2>
							<p>
								{data.count === 1
									? "1 pedigree report"
									: `${data.count} pedigree reports`}
							</p>
						</div>
						{!loading && visibleReports.length > 0 ? (
							<div className="pedigree-reports__pagination">
								<span>
									{startIndex}–{endIndex} of {data.count}
								</span>
								<Pagination
									currentPage={currentPage}
									totalPages={totalPages}
									onPageChange={handlePageChange}
								/>
							</div>
						) : null}
					</div>

					{loading ? (
						<div className="pedigree-reports-loading" role="status">
							<span className="pedigree-reports-loading__spinner" />
							<span>Loading your reports…</span>
						</div>
					) : visibleReports.length > 0 ? (
						<>
							<div className="pedigree-reports-list__labels" aria-hidden="true">
								<span>Report</span>
								<span>Status</span>
								<span>Actions</span>
							</div>
							<ul className="pedigree-reports-list">
								{visibleReports.map((report) => {
									const reportName = report.name || "Untitled pedigree";
									const status = getReportStatus(report);
									const reportPath =
										report.report?.id && report.report?.status === "completed"
											? `/reports/${report.report.id}`
											: null;
									const sireName =
										report.report?.sire?.name || report.report?.sire?.display_name;
									const damName =
										report.report?.dam?.name || report.report?.dam?.display_name;
									const parentNames = [
										sireName ? `Sire: ${sireName}` : null,
										damName ? `Dam: ${damName}` : null,
									]
										.filter(Boolean)
										.join(" · ");

									return (
										<li key={report.id}>
											<span className="pedigree-reports-list__icon">
												<i className="fa fa-dna" aria-hidden="true" />
											</span>
											<span className="pedigree-reports-list__details">
												<strong>
													{reportPath ? <Link to={reportPath}>{reportName}</Link> : reportName}
												</strong>
												<span>Created {formatDate(report.created)}</span>
												{parentNames ? (
													<span className="pedigree-reports-list__parents">
														{parentNames}
													</span>
												) : null}
											</span>
											<span
												className={`pedigree-report-status pedigree-report-status--${status.modifier}`}
											>
												{status.label}
											</span>
											<span className="pedigree-reports-list__actions">
												{reportPath ? (
													<Link
														className="pedigree-report-action pedigree-report-action--view"
														to={reportPath}
														aria-label={`View ${reportName} report`}
														title="View report"
													>
														<i className="fa fa-eye" aria-hidden="true" />
													</Link>
												) : null}
												{!reportPath && status.modifier === "pending" ? (
													<span
														className="pedigree-report-action pedigree-report-action--waiting"
														title="The report is not ready yet"
														aria-label="Report not ready"
													>
														<i className="fa fa-clock" aria-hidden="true" />
													</span>
												) : null}
											<button
													type="button"
													className="pedigree-report-action pedigree-report-action--delete"
													onClick={() => handleDelete(report)}
													aria-label={`Delete ${reportName} report`}
													title="Delete report"
												>
													<i className="fa fa-trash" aria-hidden="true" />
												</button>
											</span>
										</li>
									);
								})}
							</ul>
							{totalPages > 1 ? (
								<div className="pedigree-reports__pagination pedigree-reports__pagination--footer">
									<span>
										{startIndex}–{endIndex} of {data.count}
									</span>
									<Pagination
										currentPage={currentPage}
										totalPages={totalPages}
										onPageChange={handlePageChange}
									/>
								</div>
							) : null}
						</>
					) : (
						<div className="pedigree-reports-empty">
							<span className="pedigree-reports-empty__icon">
								<i className="fa fa-dna" aria-hidden="true" />
							</span>
						<h3>{searchTerm ? "No matching reports" : "No pedigree reports yet"}</h3>
						<p>
							{searchTerm ? "Try a different report or horse name." : "Create your first report to analyse a mating and see its results here."}
						</p>
						{searchTerm ? <button type="button" onClick={clearSearch}>Clear search</button> : <Link to="/new-pedigree">Create your first pedigree</Link>}
						</div>
					)}
				</section>
			</main>
			<SourAlert {...alertProps} />
		</MainStructure>
	);
}

export default Pedigree;
